// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  Grid,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  styled,
  Typography
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'

// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import { CustomModal } from '../../../common/CustomModal'

// others
import {
  BillingType,
  PaymentMethod,
  useFetchOperatingAccountLazyQuery,
  useFetchRemainingFlatFeeLazyQuery,
  useFetchRetainerAccountLazyQuery,
  useGetCaseBillingPauseDateLazyQuery,
  useRecordManualPaymentMutation
} from '../../../../../generated'
import { BAD_USER_INPUT, CANCEL, GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, SOMETHING_WENT_WRONG } from '../../../../constants'
import { RecordPaymentFormValues } from '../../../../interfaceTypes/forms'
import { recordPaymentValidationSchema } from '../../../../validationSchema'
import { formatCurrency, formatToTwoDecimals } from '../../../../utils'
import dayjs from 'dayjs'
import AccountDropdown from '../../../common/AccountDropdown'
import { RecordPaymentModalProps } from '../../../../interfaceTypes/props'
import PaymentMethodController from '../../../common/PaymentMethodController'
import CheckboxController from '../../../common/CheckboxController'
import InfoTooltip from '../../../common/InfoTooltip'
import OfficeAutoComplete from '../../../offices/OfficeAutocomplete'
import { CustomDatePickerController } from '../../../common/CustomDatePickerRController'

const availablePaymentMethods = [PaymentMethod.Cash, PaymentMethod.Cheque, PaymentMethod.Card, PaymentMethod.Other]

const CustomTable = styled(Table)(({ theme }) => ({
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5),
    fontSize: '14px !important'
  },
  '& .MuiTableRow-root > .MuiTableCell-root:first-of-type': {
    borderRight: '1px solid #E0E0E0',
    fontWeight: '500'
  }
}))

export const RecordPaymentModal: FC<RecordPaymentModalProps> = ({
  billId,
  clientId,
  clientPayableAmount,
  isOpen,
  billingType,
  caseId,
  onClose,
  onSuccess,
  paymentMethods,
  monthlyFlatFeeAmount,
  account,
  isFundRequest = false
}): JSX.Element => {
  const [remainingFlatFee, setRemainingFlatFee] = useState(0)
  const methods = useForm<RecordPaymentFormValues>({
    mode: 'all',
    resolver: yupResolver(recordPaymentValidationSchema),
    defaultValues: {
      amount: '',
      isAdvancePayment: false,
      date: new Date().toISOString().split('T')[0],
      paymentMethod: PaymentMethod.Cheque,
      referenceNo: '',
      slipNumber: '',
      description: '',
      destinationAccount:
        !isFundRequest && account?.active
          ? {
              value: account?.id,
              label: account?.name || ''
            }
          : undefined,
      pauseBillingUntil: null
    }
  })

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = methods
  const isFlatFee = billingType === BillingType.FlatFee && caseId
  const isAdvancePayment = watch('isAdvancePayment')
  const paymentAmount = Number(watch('amount')) || 0
  const contractAfterAmount = Math.max(remainingFlatFee - paymentAmount, 0)
  const amountToBeAdjusted = useMemo(
    () => Math.max(formatToTwoDecimals(paymentAmount - (clientPayableAmount || 0)), 0),
    [clientPayableAmount, paymentAmount]
  )

  const [fetchRemainingFlatFee] = useFetchRemainingFlatFeeLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      if (data.fetchRemainingFlatFee.remainingFlatFee) {
        setRemainingFlatFee(data.fetchRemainingFlatFee.remainingFlatFee)
      }
    }
  })

  const [getCaseBillingPauseDate] = useGetCaseBillingPauseDateLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      if (data.getCaseBillingPauseDate?.date) {
        setValue('pauseBillingUntil', new Date(data.getCaseBillingPauseDate.date).toISOString().split('T')[0])
      }
    }
  })

  const [fetchOperatingAccount] = useFetchOperatingAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchOperatingAccount) {
        setValue('destinationAccount', {
          value: data.fetchOperatingAccount.id,
          label: data.fetchOperatingAccount.name || ''
        })
      }
    }
  })

  const [fetchRetainerAccount] = useFetchRetainerAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchRetainerAccount) {
        setValue('destinationAccount', {
          value: data.fetchRetainerAccount.id,
          label: data.fetchRetainerAccount.name || ''
        })
      }
    }
  })

  const [recordPaymentMutation, { loading }] = useRecordManualPaymentMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { code } = extensions
            if (code === BAD_USER_INPUT) {
              Alert.error(error.message)
            } else {
              Alert.error(SOMETHING_WENT_WRONG)
            }
          }

          return null
        })
      } else {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },
    onCompleted(data) {
      if (data) {
        const {
          recordManualPayment: { response }
        } = data

        if (response) {
          const { status } = response

          if (status === 200) {
            Alert.success('Payment recorded successfully')
            onClose()
            onSuccess()
          }
        }
      }
    }
  })

  const toLocalDateTime = (dateString: string): Date => {
    const now = dayjs()
    return dayjs(dateString)
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond())
      .toDate()
  }

  const onSubmit: SubmitHandler<RecordPaymentFormValues> = async data => {
    const {
      amount: amountInput,
      description,
      date,
      paymentMethod,
      referenceNo,
      slipNumber,
      destinationAccount,
      pauseBillingUntil,
      isAdvancePayment,
      location
    } = data

    const dateTime = toLocalDateTime(date)

    if (!clientId) {
      Alert.error('Something went wrong, please try again later.')
      return
    }

    let amount = amountInput
    let advancePaymentAmount = 0
    let maxAmountLimit = clientPayableAmount || 0

    if (isFlatFee && isAdvancePayment && amountToBeAdjusted) {
      maxAmountLimit = remainingFlatFee
      amount = clientPayableAmount || 0
      advancePaymentAmount = amountToBeAdjusted
    }

    if (Number(amount) + advancePaymentAmount > maxAmountLimit) {
      Alert.error(`Amount must be ${formatCurrency(maxAmountLimit)} or less.`)
      return
    }

    await recordPaymentMutation({
      variables: {
        recordManualPaymentInput: {
          billId: billId || 0,
          clientId,
          amount: amount || 0,
          ...(advancePaymentAmount ? { advancePaymentAmount, pauseBillingUntil } : undefined),
          date: dateTime,
          paymentMethod,
          referenceNo,
          slipNumber: slipNumber || null,
          description,
          destinationAccountId: destinationAccount.value,
          locationId: location?.value
        }
      }
    })
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (billingType === BillingType.FlatFee && caseId) {
      fetchRemainingFlatFee({
        variables: {
          fetchRemainingFlatFeeInput: {
            caseId
          }
        }
      })
    }
  }, [billingType, caseId, fetchRemainingFlatFee])

  useEffect(() => {
    if (isAdvancePayment) {
      setValue('amount', remainingFlatFee)
    } else {
      setValue('amount', clientPayableAmount || 0)
    }
  }, [setValue, remainingFlatFee, clientPayableAmount, isAdvancePayment])

  useEffect(() => {
    if (isAdvancePayment && caseId && remainingFlatFee) {
      getCaseBillingPauseDate({
        variables: {
          advancePayment: amountToBeAdjusted,
          caseId
        }
      })
    }
  }, [isAdvancePayment, caseId, getCaseBillingPauseDate, remainingFlatFee, clientPayableAmount, amountToBeAdjusted])

  useEffect(() => {
    if (isFundRequest) {
      fetchRetainerAccount()
    } else if (!account?.active) {
      fetchOperatingAccount()
    }
  }, [isFundRequest, account?.active, fetchRetainerAccount, fetchOperatingAccount])

  return (
    <CustomModal
      title="Record Payment"
      isOpen={isOpen}
      handleClose={handleClose}
      maxWidth={isAdvancePayment ? 'xl' : 'md'}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {isFlatFee && !isFundRequest && (
              <Grid item xs={12}>
                <CheckboxController name="isAdvancePayment" label="Advance Payment" size="small" />
              </Grid>
            )}

            <Grid container item xs={12} lg={isAdvancePayment ? 6 : 12} spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomController
                  dollarAdornment
                  controllerName="amount"
                  controllerLabel="Amount"
                  fieldType="number"
                  sx={{ my: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomDatePickerController controllerName="date" controllerLabel="Date" sx={{ my: 0 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <PaymentMethodController
                  name="paymentMethod"
                  label="Payment By"
                  paymentMethods={paymentMethods ?? availablePaymentMethods}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomController
                  controllerName="referenceNo"
                  controllerLabel="Reference No."
                  fieldType="text"
                  sx={{ my: 0 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <Controller
                    name="destinationAccount"
                    control={control}
                    render={({ field }) => (
                      <AccountDropdown
                        value={field.value}
                        onChange={field.onChange}
                        searchFilters={{
                          isBankAccount: true
                        }}
                        errorMessage={errors.destinationAccount?.message || errors.destinationAccount?.value?.message}
                        label={'Destination Account'}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <OfficeAutoComplete name="location" label="Location of Payment" autoSelectOnTab />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomController controllerName="slipNumber" controllerLabel="Slip No." fieldType="text" />
              </Grid>

              <Grid item xs={12}>
                <CustomController
                  controllerName="description"
                  controllerLabel="Notes"
                  fieldType="textarea"
                  isMultiLine
                  rowsLength={4}
                  sx={{ my: 0 }}
                />
              </Grid>
            </Grid>

            {isAdvancePayment && (
              <Grid container item xs={12} lg={6} alignContent={'flex-start'} spacing={3}>
                <Grid item xs={12}>
                  <Typography fontSize={14}>
                    Bill's due amount is <b>{formatCurrency(clientPayableAmount || 0)}</b> but client is paying{' '}
                    <b>{formatCurrency(paymentAmount)}</b> so remaining <b>{formatCurrency(amountToBeAdjusted)}</b> will
                    be adjusted towards his remaining contract balance.
                  </Typography>

                  <Typography fontSize={14} mt={1.8} mb={0.5}>
                    <b>Amount to be adjusted:</b> {formatCurrency(amountToBeAdjusted)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <CustomDatePickerController
                    controllerName="pauseBillingUntil"
                    controllerLabel="Billing Paused Until"
                    sx={{ my: 0 }}
                  />
                  <InfoTooltip info="Billing paused means that the monthly retainer amount and special payments (if any) will not be charged until the date selected in this field. However, all the expense entries, cost entries or other legal fee entries will still be part of the monthly bill." />
                </Grid>

                <Grid item xs={12}>
                  <TableContainer>
                    <CustomTable>
                      <TableBody>
                        <TableRow>
                          <TableCell>Current Contract Balance before Adjustment: </TableCell>
                          <TableCell>{formatCurrency(remainingFlatFee)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Remaining Contract Balance after Adjustment</TableCell>
                          <TableCell>{formatCurrency(contractAfterAmount)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </CustomTable>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose}>{CANCEL}</Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading}
              endIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              Record Transaction
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default RecordPaymentModal
